import React, { useRef } from "react";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { BetaPill } from "src/base-components/BetaPill";
import { Card } from "src/base-components/Card";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Switch } from "src/base-components/Switch";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";
import { Toolbar } from "src/base-components/TextEditor/Toolbar";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { CaseTemplateVersionCopilotSettingsOperatingModeEnum } from "src/clients/case-management";
import { Accordion } from "src/design-system/Accordion";
import { EmptyState } from "src/design-system/EmptyState";
import { useIsAgentsEnabled } from "src/hooks/useIsAgentsEnabled";

const COPILOT_PROMPT_PLACEHOLDER = `Add instructions specific to your review process. Examples:

CASE TYPE & CONTEXT
Describe what these cases are about so the copilot understands the domain.
e.g., "These are AML transaction monitoring alerts. Each case represents a cluster of transactions flagged for potential suspicious activity under BSA/AML regulations."

REVIEW PRIORITIES
What should the copilot focus on when analyzing a case?
e.g., "Prioritize transaction volume and velocity anomalies over individual transaction amounts. Flag anything that doesn't fit a typical document structure."

THRESHOLDS & DECISION GUIDANCE
Reference key thresholds or rules the copilot should apply when making recommendations.
e.g., "CTR filing required for cash transactions over $10,000. For credit underwriting, approve if DTI < 43% and credit score > 680. Escalate if PEP match confidence > 80%."

TASKS & FORMS
Use the descriptions on your tasks and form fields to guide the copilot's recommendations. Add any extra context here that isn't captured in those descriptions.
e.g., "When filling the risk assessment task, the 'risk_rating' field should reflect the overall transaction pattern, not just the single highest-value transaction. For the 'disposition_reason' field, always reference at least two supporting data points."

TERMINOLOGY
Define terms or naming conventions your team uses.
e.g., "Use 'alert' not 'case' for the initial trigger. Say 'suspicious' not 'fraudulent' until a SAR is filed. Refer to customers as 'members'."

DOCUMENTS & FILES
Tell the copilot which attached documents to prioritize and how to use them. Reference documents by their file name or category.
e.g., "Prioritize 'bank_statement_*.pdf' files — cross-reference deposit amounts against reported income. For documents in the 'Identity' category, verify name and address consistency with the application data."`;

export const CopilotConfigurationStep: React.FC = () => {
  const step = getStepById("configure-copilot");
  const { setValue, getValues } = useFormContext<CaseTemplateFormValues>();
  const toolbarRef = useRef<HTMLUListElement>(null);
  const hasPermission = useCheckFieldPermission();
  const { isEnabled: isAgentsEnabled } = useIsAgentsEnabled();

  const copilotEnabled = useWatch<CaseTemplateFormValues>({
    name: "attributes.copilot_settings.copilot_enabled",
  });

  return (
    <FormStepWrapper
      actions={
        isAgentsEnabled && (
          <Controller
            name="attributes.copilot_settings.copilot_enabled"
            render={({ field }) => (
              <Switch
                dataLoc="copilot-enable-toggle"
                disabled={
                  !hasPermission(
                    "edit",
                    "attributes.copilot_settings.copilot_enabled",
                  )
                }
                enabled={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
        )
      }
      description={step.description}
      docLink={step.docLink}
      title={
        <span className="flex items-center gap-2">
          {step.title} <BetaPill />
        </span>
      }
    >
      {!isAgentsEnabled ? (
        <EmptyState
          dataLoc="copilot-not-available-empty-state"
          description="To use Copilot, your workspace needs the Agents module enabled. Reach out to your Taktile contact to get access."
          headline="Copilot is not available"
          icon={faSparkles}
        />
      ) : copilotEnabled ? (
        <Accordion defaultValue={["custom-instructions"]}>
          <Accordion.Item
            dataLoc="copilot-custom-instructions"
            description="Define how Copilot should behave for cases using this template. Copilot will use this knowledge to drive its logic and provide specific details during cases."
            label="Custom instructions"
            value="custom-instructions"
          >
            <Card>
              <Card.Content>
                <div className="flex flex-col gap-y-5">
                  <div className="flex flex-col gap-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-inter-semibold-13px text-gray-800">
                        Prompt
                      </span>
                      <CopyTextIcon
                        dataLoc="copilot-prompt-copy"
                        tooltip="Copy prompt"
                        value={() =>
                          (getValues(
                            "attributes.copilot_settings.copilot_prompt",
                          ) as string) ?? ""
                        }
                        preventDefault
                      />
                    </div>
                    <div
                      className={twJoin(
                        "flex min-w-0 flex-1 flex-col rounded border border-gray-200 bg-white p-3",
                        "group focus-within:border-indigo-600 focus-within:ring-2 focus-within:ring-indigo-500/25",
                      )}
                    >
                      <div className="h-[350px]">
                        <Controller
                          name="attributes.copilot_settings.copilot_prompt"
                          render={({ field }) => (
                            <TextEditor
                              dataLoc="copilot-instructions-prompt"
                              maxHeight="max-h-[350px]"
                              placeholder={COPILOT_PROMPT_PLACEHOLDER}
                              readOnly={
                                !hasPermission(
                                  "edit",
                                  "attributes.copilot_settings.copilot_prompt",
                                )
                              }
                              toolbarRef={toolbarRef}
                              value={field.value as string}
                              fullHeight
                              keepEmptyLines
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                      <div className="flex justify-between gap-x-3 pt-3">
                        <Toolbar
                          ref={toolbarRef}
                          exclude={["heading"]}
                          faded={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </Accordion.Item>

          <Accordion.Item
            dataLoc="copilot-autonomous-investigation"
            description={
              <span>
                When enabled, Copilot proactively starts the review once a
                reviewer is assigned to the case. At every status change, it
                recommends next steps and pre-fills task and action forms. All
                final actions and submissions still require the reviewer's
                manual approval. This applies to live cases only.
              </span>
            }
            rightSlot={
              <Controller
                name="attributes.copilot_settings.operating_mode"
                render={({ field }) => (
                  <Switch
                    dataLoc="copilot-autonomous-investigation-toggle"
                    disabled={
                      !hasPermission(
                        "edit",
                        "attributes.copilot_settings.operating_mode",
                      )
                    }
                    enabled={
                      field.value ===
                      CaseTemplateVersionCopilotSettingsOperatingModeEnum.EAGER
                    }
                    onChange={(checked) =>
                      field.onChange(
                        checked
                          ? CaseTemplateVersionCopilotSettingsOperatingModeEnum.EAGER
                          : CaseTemplateVersionCopilotSettingsOperatingModeEnum.LAZY,
                      )
                    }
                  />
                )}
              />
            }
            label="Copilot Auto-Suggestions"
            value="autonomous-investigation"
          />
        </Accordion>
      ) : (
        <EmptyState
          actionButton={{
            text: "Enable Copilot",
            disabled: !hasPermission(
              "edit",
              "attributes.copilot_settings.copilot_enabled",
            ),
            onClick: () =>
              setValue("attributes.copilot_settings.copilot_enabled", true),
          }}
          dataLoc="copilot-disabled-empty-state"
          description="Copilot must be enabled to complete configuration for this template."
          headline="Copilot is not enabled"
          icon={faSparkles}
        />
      )}
    </FormStepWrapper>
  );
};
